import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Signal,
  Wifi,
  BatteryFull,
  PhoneCall,
  Voicemail,
  ClipboardCheck,
  TrendingUp,
  Check,
  ArrowRight,
  Bot,
  UserPlus
} from 'lucide-react';
import './AIPhoneAssistant.css';

const EASE = [0.22, 0.61, 0.36, 1];
const APP_ICON = '/favicons/apple-touch-icon.png';

const BENEFITS = [
  { icon: Voicemail, title: 'No more voicemail', text: 'Callers reach AI, not a beep' },
  { icon: PhoneCall, title: 'Answers when you can’t', text: 'Every call, day or night' },
  { icon: ClipboardCheck, title: 'Captures every detail', text: 'Name, job and time' },
  { icon: TrendingUp, title: 'More bookings', text: 'No missed-call revenue lost' }
];

const CAPTURED = [
  ['Name', 'John Smith'],
  ['Party', '4 guests'],
  ['When', 'Tonight · 7 PM']
];

/* Loop the on-phone call, paused offscreen / on hidden tab; off under reduced motion. */
function useLoop(ref, reduce, period) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const inView = { current: true };
    const visible = { current: true };
    let t = null;
    const clear = () => { if (t) { clearTimeout(t); t = null; } };
    const sched = () => {
      clear();
      if (!inView.current || !visible.current) return;
      t = setTimeout(() => { setTick((x) => x + 1); sched(); }, period);
    };
    let io = null;
    if (ref.current && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (e) => { inView.current = e[0]?.isIntersecting ?? true; sched(); },
        { threshold: 0.25 }
      );
      io.observe(ref.current);
    }
    const onVis = () => { visible.current = !document.hidden; sched(); };
    document.addEventListener('visibilitychange', onVis);
    sched();
    return () => { clear(); if (io) io.disconnect(); document.removeEventListener('visibilitychange', onVis); };
  }, [ref, reduce, period]);
  return tick;
}

function AiCall({ reduce, tick }) {
  const step = (s) =>
    reduce
      ? {}
      : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.34, delay: s, ease: EASE } };

  return (
    <div className="vai-call-screen" key={tick}>
      <div className="vai-incoming">
        <span className="vai-incoming-ic"><Voicemail size={15} strokeWidth={2.3} /></span>
        <div className="vai-incoming-main">
          <span className="vai-incoming-title">Incoming call · (904) 555-0182</span>
          <span className="vai-incoming-sub">
            <s><span className="vai-vm-full">Heading to voicemail</span><span className="vai-vm-short">Voicemail</span></s> <b>AI answered</b>
            <span className="vai-wave" aria-hidden="true"><i /><i /><i /><i /></span>
          </span>
        </div>
      </div>

      <div className="vai-chat">
        <motion.div className="vai-bubble vai-bubble-ai" {...step(0.25)}>Thanks for calling Coastal Kitchen! How can I help?</motion.div>
        <motion.div className="vai-bubble vai-bubble-cust" {...step(1.0)}>Can I book a table for 4 tonight?</motion.div>
        <motion.div className="vai-bubble vai-bubble-ai" {...step(1.75)}>Yes! I have 7:00 PM open. Want me to book it?</motion.div>
      </div>

      <motion.div className="vai-captured" {...step(2.4)}>
        <div className="vai-captured-head"><Bot size={13} strokeWidth={2.3} />Captured</div>
        {CAPTURED.map(([k, v], i) => (
          <motion.div className="vai-field" key={k} {...step(2.6 + i * 0.18)}>
            <span className="vai-field-k">{k}</span>
            <span className="vai-field-v">{v}</span>
            <Check size={13} strokeWidth={2.8} className="vai-field-ck" />
          </motion.div>
        ))}
      </motion.div>

      <motion.div className="vai-done" {...step(3.35)}>
        <UserPlus size={13} strokeWidth={2.4} />
        <span>Reservation sent to</span>
        <img className="vai-done-logo" src="/hero-app/opentable.png" alt="OpenTable" />
        <Check size={14} strokeWidth={2.8} className="vai-done-ck" />
      </motion.div>
    </div>
  );
}

function AIPhoneAssistant() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const tick = useLoop(ref, reduce, 7000);

  const rise = (d) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration: 0.6, delay: d, ease: EASE }
        };

  return (
    <section className="vai" ref={ref} aria-label="AI phone assistant: never miss another lead">
      <div className="vai-inner">
        <motion.div
          className="vai-media"
          aria-hidden="true"
          initial={reduce ? false : { opacity: 0, y: 30, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="vai-glow" />
          <div className="vai-float">
          <div className="vai-device">
            <div className="vai-screen">
              <div className="vai-statusbar">
                <span className="vai-time">9:41</span>
                <span className="vai-status-ic">
                  <Signal size={12} strokeWidth={2.4} />
                  <Wifi size={12} strokeWidth={2.4} />
                  <BatteryFull size={15} strokeWidth={2} />
                </span>
              </div>
              <div className="vai-appbar">
                <span className="vai-appicon"><img src={APP_ICON} alt="" draggable="false" /></span>
                <div className="vai-appbar-text">
                  <span className="vai-appname">AI Call Answering</span>
                  <span className="vai-appsub">Replaces your voicemail</span>
                </div>
                <span className="vai-livedot" />
              </div>
              <div className="vai-body">
                <AiCall reduce={reduce} tick={tick} />
              </div>
            </div>
            <img className="vai-frame" src="/phone-frame.avif" alt="" aria-hidden="true" draggable="false" />
          </div>
          </div>
        </motion.div>

        <div className="vai-text">
          <motion.span className="vai-eyebrow" {...rise(0)}>AI phone assistant</motion.span>
          <motion.h2 className="vai-title" {...rise(0.05)}>
            Never miss another <span className="vai-gold">lead</span>.
          </motion.h2>
          <motion.p className="vai-desc" {...rise(0.1)}>
            When you can&rsquo;t pick up, callers don&rsquo;t get dumped to voicemail. Your AI answers
            instead, captures every detail, and books the job automatically.
          </motion.p>
          <motion.ul className="vai-benefits" {...rise(0.15)}>
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <span className="vai-benefit-ic"><Icon size={16} strokeWidth={2.3} /></span>
                <div className="vai-benefit-main">
                  <span className="vai-benefit-title">{title}</span>
                  <span className="vai-benefit-text">{text}</span>
                </div>
              </li>
            ))}
          </motion.ul>
          <motion.div {...rise(0.2)}>
            <Link to="/services/ai-phone-assistant" className="vai-cta">
              Get AI call answering
              <ArrowRight size={16} strokeWidth={2.4} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AIPhoneAssistant;
